import { useMemo, useState } from "react";
import { debounce } from "lodash";
import { ICustomer } from "../common/interfaces";
import { CustomerService } from "../services/CustomerService";
import { initialState } from "../context/posStore";

const MIN_DIGITS_TO_SEARCH = 3;

const digitsOnly = (value: string) => value.replace(/\D/g, "");

/**
 * Identificación de cliente por teléfono, en pasos:
 * 1. "phone": mientras se teclea, busca coincidencias (últimos 10 dígitos,
 *    ignorando lada/otros caracteres) y las muestra para seleccionar.
 * 2. "name": confirmar/editar el nombre (autocompletado si hubo match) y
 *    crear o actualizar al cliente real en la tabla clientes.
 * 3. "genericName": ruta de "Público en General" — pide solo un nombre para
 *    identificar el pedido, que NO se guarda en clientes, solo en las notas
 *    de la orden (order.notes_order), independiente del registro de cliente.
 * Seleccionar una coincidencia o presionar Enter avanza al paso 2.
 */
export const useCustomerIdentification = () => {
  const [step, setStep] = useState<"phone" | "name" | "genericName">("phone");
  const [phone, setPhoneRaw] = useState("");
  const [name, setName] = useState("");
  const [genericName, setGenericName] = useState("");
  const [found, setFound] = useState<ICustomer | null>(null);
  const [matches, setMatches] = useState<ICustomer[]>([]);
  const [searching, setSearching] = useState(false);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        CustomerService.searchByPhoneFragment(digitsOnly(value))
          .then((res) => setMatches(res.object ?? []))
          .finally(() => setSearching(false));
      }, 300),
    []
  );

  const setPhone = (value: string) => {
    setPhoneRaw(value);
    setFound(null);
    const digits = digitsOnly(value);
    if (digits.length < MIN_DIGITS_TO_SEARCH) {
      setMatches([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debouncedSearch(value);
  };

  const selectMatch = (customer: ICustomer) => {
    setPhoneRaw(customer.telefono ?? "");
    setName(customer.nombre);
    setFound(customer);
    setMatches([]);
    setStep("name");
  };

  const advanceToName = () => {
    setMatches([]);
    setStep("name");
  };

  const startGeneric = () => {
    setMatches([]);
    setStep("genericName");
  };

  const resolveCustomer = async (): Promise<ICustomer | undefined> => {
    if (found) {
      if (found.nombre !== name) {
        await CustomerService.updateCustomerByPhone({
          nombre: name,
          telefono: phone,
          direccion: found.direccion,
        });
      }
      return { ...found, nombre: name };
    }
    const res = await CustomerService.createCustomer({
      nombre: name,
      rfc: "XAXX010101000",
      telefono: phone,
      direccion: "",
    });
    return res.object;
  };

  const reset = () => {
    setStep("phone");
    setPhoneRaw("");
    setName("");
    setGenericName("");
    setFound(null);
    setMatches([]);
  };

  return {
    step,
    phone,
    name,
    setName,
    genericName,
    setGenericName,
    found,
    matches,
    searching,
    setPhone,
    selectMatch,
    advanceToName,
    startGeneric,
    resolveCustomer,
    reset,
    genericCustomer: initialState.customer,
  };
};
